import axios from 'axios';
import store from 'store';
import jwtDecode from 'jwt-decode';
import qs from 'qs';

export class DataService {
  #testMode = false;

  /**
   * @param {Object} options - options object
   * @param {string} options.authStoreKey - key name of authorization token, e.g. 'api-token'
   * @param {string} options.baseUrl - Api base url
   */
  constructor(options = {}) {

    let settings = {
      authStoreKey: null,
      baseUrl: null
    };

    this.settings = Object.assign(settings, options);

    if(!this.settings.authStoreKey) {
      throw Error('#constructor: settings.authStoreId needs to be set');
    }

    if(!this.settings.baseUrl) {
      throw Error('#constructor: settings.baseUrl needs to be set');
    }

    this.CancelToken = axios.CancelToken;
    this.cancelRequestFn = null;


    this.axios = axios.create({
      baseURL: this.settings.baseUrl,
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      validateStatus: function(status) {
        return status < 500; // Reject only if the status code is greater than or equal to 500
      }
    });

    // For debugging

    this.axios.interceptors.request.use((config) => {

      // Add test param if in test mode
      if(this.#testMode) {
        config.params.testMode = 1;
      }

      console.log('Full API request', config);
      return config;
    });

    this.axios.interceptors.response.use(function(response) {
      console.log('Full API response', response);
      return response;
    }, function(error) {
      console.log('API error (>=500)', error);
      return Promise.reject(error);
    });

    const token = store.get(this.settings.authStoreKey);
    if(token) {
      this.setAuthToken(token);
    }
  }

  setTestMode(bool) {
    this.#testMode = bool;
  }

  // RESTful methods

  get(path, params) {
    console.log('dataservice.get', path, params);
    return this._request('get', path, null, params);
  }

  put(path, data) {
    return this._request('put', path, data);
  }

  post(path, data) {
    return this._request('post', path, data);
  }

  patch(path, data) {
    return this._request('patch', path, data);
  }

  del(path, data) {
    return this._request('delete', path, data);
  }

  // AUTH

  isLoggedIn() {
    // Checks if there is a saved token and it's still valid
    const token = this.getAuthToken();
    return !!token && !this.isAuthTokenExpired(token);
  }

  getAuthToken() {
    return store.get(this.settings.authStoreKey);
  }

  setAuthToken(token) {
    this.axios.defaults.headers.common['Authorization'] = 'Bearer ' + token;
    store.set(this.settings.authStoreKey, token);
  }

  clearAuthToken() {
    delete this.axios.defaults.headers.common['Authorization'];
    store.remove(this.settings.authStoreKey);
  }

  isAuthTokenExpired(token) {
    try {
      const decoded = jwtDecode(token);
      if(decoded.exp < Date.now() / 1000) { // Checking if token is expired.
        return true;
      }

      return false;
    } catch(err) {
      return false;
    }
  }

  _request(method, path, data = {}, params = {}) {
    return this.axios({
      method,
      url: path,
      data,
      params,
      paramsSerializer: params => {
        // Support for nested params
        //
        // {
        //   searchOperator: 'AND',
        //   params: [
        //     {
        //       field: 'price',
        //       operator: 'largerThan',
        //       value: 10
        //     },
        //     {
        //       field: 'itemFamily',
        //       operator: 'oneOf',
        //       value: ['IF-1234']
        //     }
        //   ]
        // }
        //
        // ?searchOperator=AND&params[0][field]=price&params[0][operator]=largerThan...
        return qs.stringify(params);
      },
      withCredentials: true,
      cancelToken: new this.CancelToken((c) => {
        // An executor function receives a cancel function as a parameter
        this.cancelRequestFn = c;
      })
    }).then(res => {
      const data = res.data;

      if(data.code === 401) {
        // Clear auth token
        this.clearAuthToken();
      }

      return data;
    })
    //.catch(error => {
    //   if(error.response) {
    //     const data = error.response.data;
    //     console.error('Data-service: Server error', data);
    //     //console.error(`Data-service: Server error status: ${data.code}\n${data.result.error.stack}`);
    //   }
    // });
  }

  cancelRequest() {
    this.cancelRequestFn && this.cancelRequestFn('Operation canceled by the user.');
  }

}
